import { AxiosError } from 'axios'

import {
  Document,
  DocumentShareRequest,
  DocumentShareResponse,
  DocumentUploadResponse,
} from '@lyrahealth-inc/shared-app-logic/src/models/document/Document'

import { axiosAuthInstance } from '../../../common/http/axiosInstance'
import { baseApi, RTKQueryTag } from '../../../data/baseApi'

type UploadDocumentArgs = {
  file: File
  fileName: string
  documentType: string
  episodeId: string
  isShared?: boolean
  signatureRequired?: boolean
  signingFlow?: string
  returnUrl?: string
  providerId?: string
}

type DownloadDocumentArgs = {
  documentId: string
  providerId?: string
}

type DeleteDocumentArgs = {
  documentId: string
  providerId?: string
}

type ShareDocumentArgs = {
  documentId: string
  recipientId: string
  providerId?: string
}

type GetDocumentsArgs = {
  providerId: string
  patientId: string
}

type EditDocumentArgs = {
  documentId: string
  fileName: string
  documentType: string
  providerId?: string
}

type CreateDocumentFromTemplateArgs = {
  documentTemplateId: string
  episodeId: string
  isShared?: boolean
  providerId?: string
}

export type GetDraftSenderViewUrlArgs = {
  documentId: string
  returnUrl: string
  providerId?: string
}

export type GetSigningUrlArgs = {
  documentId: string
  returnUrl: string
  providerId?: string
}

const documentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    uploadDocument: builder.mutation<DocumentUploadResponse, UploadDocumentArgs>({
      queryFn: async ({
        file,
        fileName,
        documentType,
        episodeId,
        isShared,
        signatureRequired,
        signingFlow,
        returnUrl,
        providerId,
      }) => {
        try {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('file_name', fileName)
          formData.append('document_type', documentType)
          formData.append('episode_id', episodeId)
          if (isShared !== undefined) {
            formData.append('is_shared', String(isShared))
          }
          if (signatureRequired !== undefined) {
            formData.append('signature_required', String(signatureRequired))
          }
          if (signingFlow) {
            formData.append('signing_flow', signingFlow)
          }
          if (returnUrl) {
            formData.append('return_url', returnUrl)
          }

          const url = providerId ? `/lt/v1/documents?provider_id=${encodeURIComponent(providerId)}` : '/lt/v1/documents'
          const response = await axiosAuthInstance.post<DocumentUploadResponse>(url, formData)
          return { data: response.data }
        } catch (error) {
          const axiosError = error as AxiosError
          return {
            error: {
              status: axiosError.response?.status,
              data: axiosError.response?.data,
              message: axiosError.message,
            },
          }
        }
      },
      invalidatesTags: [RTKQueryTag.DOCUMENT],
    }),
    downloadDocument: builder.query<Blob, DownloadDocumentArgs>({
      query: ({ documentId, providerId }) => ({
        method: 'get',
        url: `/lt/v1/documents/${documentId}`,
        responseType: 'blob',
        ...(providerId && { params: { provider_id: providerId } }),
      }),
    }),
    deleteDocument: builder.mutation<void, DeleteDocumentArgs>({
      query: ({ documentId, providerId }) => ({
        method: 'delete',
        url: `/lt/v1/documents/${documentId}`,
        ...(providerId && { params: { provider_id: providerId } }),
      }),
      invalidatesTags: [RTKQueryTag.DOCUMENT],
    }),
    shareDocument: builder.mutation<DocumentShareResponse, ShareDocumentArgs>({
      query: ({ documentId, recipientId, providerId }) => ({
        method: 'post',
        url: `/lt/v1/documents/${documentId}/share`,
        data: { recipient_id: recipientId } as DocumentShareRequest,
        ...(providerId && { params: { provider_id: providerId } }),
      }),
      invalidatesTags: [RTKQueryTag.DOCUMENT],
    }),
    getDocuments: builder.query<{ documents: Document[] }, GetDocumentsArgs>({
      query: ({ providerId, patientId }) => ({
        method: 'get',
        url: `/lt/v1/provider/${providerId}/patient/${patientId}/documents`,
      }),
      providesTags: [RTKQueryTag.DOCUMENT],
    }),
    editDocument: builder.mutation<void, EditDocumentArgs>({
      query: ({ documentId, fileName, documentType, providerId }) => ({
        method: 'patch',
        url: `/lt/v1/documents/${documentId}`,
        data: { file_name: fileName, document_type: documentType },
        ...(providerId && { params: { provider_id: providerId } }),
      }),
      invalidatesTags: [RTKQueryTag.DOCUMENT],
    }),
    createDocumentFromTemplate: builder.mutation<DocumentUploadResponse, CreateDocumentFromTemplateArgs>({
      query: ({ documentTemplateId, episodeId, isShared, providerId }) => ({
        method: 'post',
        url: `/lt/v1/documents/templates/${documentTemplateId}/documents`,
        data: {
          episode_id: episodeId,
          ...(isShared !== undefined && { is_shared: isShared }),
        },
        showAlert: false,
        ...(providerId && { params: { provider_id: providerId } }),
      }),
      invalidatesTags: [RTKQueryTag.DOCUMENT],
    }),
    // Both getDraftSenderViewUrl and getSigningUrl hit the same endpoint but receive different response shapes:
    // - getDraftSenderViewUrl: called when the provider is the sender setting up signature locations (DRAFT state) → backend returns { sender_view_url }
    // - getSigningUrl: called when the provider is a recipient signing the document (SENT state, patient has already signed) → backend returns { signing_url }
    getDraftSenderViewUrl: builder.query<{ sender_view_url: string }, GetDraftSenderViewUrlArgs>({
      query: ({ documentId, returnUrl, providerId }) => ({
        method: 'get',
        url: `/lt/v1/documents/${documentId}`,
        params: { return_url: returnUrl, ...(providerId && { provider_id: providerId }) },
      }),
      keepUnusedDataFor: 0, // prevents caching the URL because DocuSign sender view URLs are time-limited and must always be fetched fresh.
    }),
    getSigningUrl: builder.query<{ signing_url: string }, GetSigningUrlArgs>({
      query: ({ documentId, returnUrl, providerId }) => ({
        method: 'get',
        url: `/lt/v1/documents/${documentId}`,
        params: { return_url: returnUrl, ...(providerId && { provider_id: providerId }) },
      }),
      keepUnusedDataFor: 0, // DocuSign signing URLs are single-use and must always be fetched fresh.
    }),
  }),
})

export const {
  useUploadDocumentMutation,
  useLazyDownloadDocumentQuery,
  useDeleteDocumentMutation,
  useShareDocumentMutation,
  useGetDocumentsQuery,
  useEditDocumentMutation,
  useCreateDocumentFromTemplateMutation,
  useLazyGetDraftSenderViewUrlQuery,
  useLazyGetSigningUrlQuery,
} = documentsApi

export default documentsApi
